/** Group admin: categories, locks, adjustments, transfers, and the audit log. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageScroll } from "../../components/layout/PageScroll";
import { Spinner } from "../../components/ui/spinner";
import { useBalances, useGroup, useGroupId } from "../../components/layout/AppShell";
import { useGroups } from "../../components/layout/GroupSwitcher";
import {
  ApiError,
  api,
  type AuditEntry,
  type Lock,
  type TransferPreview,
} from "../../lib/api";
import { formatSigned, parseAmount, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";
import { useCategories, useMembers } from "../ledger/useLedger";

type Tab = "categories" | "locks" | "adjustments" | "transfers" | "audit";

const TABS: [Tab, string][] = [
  ["categories", "Categories"],
  ["locks", "Locks"],
  ["adjustments", "Adjustments"],
  ["transfers", "Transfer balances"],
  ["audit", "Audit log"],
];

export function AdminPage() {
  const groupId = useGroupId();
  const group = useGroup(groupId);
  const [tab, setTab] = useState<Tab>("categories");
  const [error, setError] = useState<string | null>(null);

  if (group.data && group.data.my_role !== "admin") {
    return (
      <p className="py-16 text-center text-sm text-muted">
        Only group admins can open this page.
      </p>
    );
  }

  return (
    <PageScroll>
      <div>
        <h1 className="display py-3 text-sm text-muted">Admin</h1>

        <div className="mb-4 flex flex-wrap gap-1" role="tablist">
          {TABS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => {
                setTab(value);
                setError(null);
              }}
              className={cn("chip tap", tab === value && "chip-on")}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? (
          <p role="alert" className="mb-3 rounded border border-owing/40 bg-owing/10 px-3 py-2 text-sm">
            {error}
          </p>
        ) : null}

        {tab === "categories" && <Categories groupId={groupId} onError={setError} />}
        {tab === "locks" && <Locks groupId={groupId} onError={setError} />}
        {tab === "adjustments" && <Adjustments groupId={groupId} onError={setError} />}
        {tab === "transfers" && <Transfers groupId={groupId} onError={setError} />}
        {tab === "audit" && <Audit groupId={groupId} />}
      </div>
    </PageScroll>
  );
}

type PanelProps = { groupId: number; onError: (message: string | null) => void };

function fail(onError: (message: string) => void) {
  return (cause: unknown) =>
    onError(cause instanceof ApiError ? cause.message : "Something went wrong.");
}

function Categories({ groupId, onError }: PanelProps) {
  const queryClient = useQueryClient();
  const categories = useCategories(groupId);
  const [name, setName] = useState("");
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["categories", groupId] });

  const add = useMutation({
    mutationFn: () => api.post(`/groups/${groupId}/categories`, { name: name.trim() }),
    onSuccess: () => {
      setName("");
      onError(null);
      void refresh();
    },
    onError: fail(onError),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/groups/${groupId}/categories/${id}`),
    onSuccess: () => void refresh(),
    onError: fail(onError),
  });

  return (
    <section>
      <p className="mb-3 text-xs text-muted">
        Adding a category changes nothing retroactively. Deleting one moves its entries to
        Misc and queues them to be categorized again.
      </p>
      <ul className="divide-y divide-rule border-y border-rule">
        {(categories.data ?? []).map((category) => (
          <li key={category.id} className="flex items-center justify-between py-2.5">
            <span>
              {category.name}
              {category.is_fallback ? (
                <span className="ml-2 text-xs text-muted">fallback</span>
              ) : null}
            </span>
            {category.is_fallback ? null : (
              <Button size="sm" variant="danger" onClick={() => remove.mutate(category.id)}>
                Delete
              </Button>
            )}
          </li>
        ))}
      </ul>
      <form
        className="mt-4 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) add.mutate();
        }}
      >
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="New category"
          aria-label="New category name"
        />
        <Button type="submit" variant="primary">
          Add
        </Button>
      </form>
    </section>
  );
}

function Locks({ groupId, onError }: PanelProps) {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");

  const locks = useQuery({
    queryKey: ["locks", groupId],
    queryFn: () => api.get<Lock[]>(`/groups/${groupId}/locks`),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["locks", groupId] });
    void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<Lock>(`/groups/${groupId}/locks`, {
        kind: "range",
        start_date: from || null,
        end_date: to || null,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      setNote("");
      onError(null);
      refresh();
    },
    onError: fail(onError),
  });

  const release = useMutation({
    mutationFn: (id: number) => api.delete(`/groups/${groupId}/locks/${id}`),
    onSuccess: refresh,
    onError: fail(onError),
  });

  return (
    <section>
      <p className="mb-3 text-xs text-muted">
        A range lock closes a period: existing entries lock, and anything backdated into it
        later arrives locked. Admins can still edit; members cannot.
      </p>
      <ul className="divide-y divide-rule border-y border-rule">
        {(locks.data ?? []).map((lock) => (
          <li key={lock.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
            <span>
              <span className="num">{lock.start_date ?? "…"}</span>
              <span className="text-muted"> → </span>
              <span className="num">{lock.end_date ?? "…"}</span>
              {lock.note ? <span className="ml-2 text-muted">{lock.note}</span> : null}
            </span>
            <Button size="sm" onClick={() => release.mutate(lock.id)}>
              Release
            </Button>
          </li>
        ))}
        {(locks.data ?? []).length === 0 ? (
          <li className="py-3 text-sm text-muted">No active locks.</li>
        ) : null}
      </ul>
      <form
        className="mt-4 flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Input
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          aria-label="Lock from"
          className="w-40"
        />
        <Input
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          aria-label="Lock to"
          className="w-40"
        />
        <Input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Note (optional)"
          aria-label="Lock note"
          className="flex-1"
        />
        <Button type="submit" variant="primary">
          Lock range
        </Button>
      </form>
    </section>
  );
}

function Adjustments({ groupId, onError }: PanelProps) {
  const members = useMembers(groupId);
  const group = useGroup(groupId);
  const balances = useBalances(groupId);
  const queryClient = useQueryClient();
  const currency = group.data?.base_currency ?? "USD";

  const [name, setName] = useState("");
  const [lines, setLines] = useState<Record<number, string>>({});

  const total = Object.values(lines).reduce(
    (sum, value) => sum + (parseAmount(value, currency) ?? 0),
    0,
  );

  const create = useMutation({
    mutationFn: () =>
      api.post(`/groups/${groupId}/adjustments`, {
        name: name.trim(),
        lines: Object.entries(lines)
          .map(([memberId, value]) => ({
            member_id: Number(memberId),
            delta_cents: parseAmount(value, currency) ?? 0,
          }))
          .filter((line) => line.delta_cents !== 0),
      }),
    onSuccess: () => {
      setName("");
      setLines({});
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      void balances.refetch();
    },
    onError: fail(onError),
  });

  return (
    <section>
      <p className="mb-3 text-xs text-muted">
        Move balance directly — for reassigning a departed ghost member, say. The lines must
        add up to zero, and the app will refuse anything else.
      </p>
      <Input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="What is this adjustment for?"
        aria-label="Adjustment name"
        className="mb-3"
      />
      <ul className="divide-y divide-rule border-y border-rule">
        {(members.data ?? []).map((member) => (
          <li key={member.id} className="flex items-center gap-3 py-2">
            <span className="w-32 shrink-0 truncate text-sm">{member.display_name}</span>
            <Input
              inputMode="decimal"
              className="h-9"
              placeholder="0.00"
              aria-label={`Adjustment for ${member.display_name}`}
              value={lines[member.id] ?? ""}
              onChange={(event) =>
                setLines((current) => ({ ...current, [member.id]: event.target.value }))
              }
            />
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-between">
        <span className={cn("num text-sm", total === 0 ? "text-muted" : "text-owing")}>
          Sum {formatSigned(total, currency)}
        </span>
        <Button
          variant="primary"
          disabled={total !== 0 || !name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          Create adjustment
        </Button>
      </div>
    </section>
  );
}

function Transfers({ groupId, onError }: PanelProps) {
  const groups = useGroups();
  const balances = useBalances(groupId);
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState<number | null>(null);
  const [archive, setArchive] = useState(true);
  const [plan, setPlan] = useState<TransferPreview | null>(null);

  const preview = useMutation({
    mutationFn: () =>
      api.post<TransferPreview>(`/groups/${groupId}/transfers/preview`, {
        target_group_id: targetId,
      }),
    onSuccess: (result) => {
      setPlan(result);
      onError(null);
    },
    onError: fail(onError),
  });

  const execute = useMutation({
    mutationFn: () =>
      api.post(`/groups/${groupId}/transfers`, {
        target_group_id: targetId,
        archive_source: archive,
      }),
    onSuccess: () => {
      setPlan(null);
      onError(null);
      void queryClient.invalidateQueries();
      void balances.refetch();
    },
    onError: fail(onError),
  });

  const others = (groups.data ?? []).filter((group) => group.id !== groupId);

  return (
    <section>
      <p className="mb-3 text-xs text-muted">
        End of a trip: move every balance into another group. Everyone with a balance needs
        somewhere to land, or the transfer is refused.
      </p>
      <div className="mb-3 flex flex-wrap gap-2">
        <select
          className="field w-56"
          aria-label="Target group"
          value={targetId ?? ""}
          onChange={(event) => setTargetId(Number(event.target.value) || null)}
        >
          <option value="">Transfer into…</option>
          {others.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        <Button disabled={!targetId} onClick={() => preview.mutate()}>
          Preview
        </Button>
      </div>

      {plan ? (
        <>
          <ul className="divide-y divide-rule border-y border-rule text-sm">
            {plan.mappings.map((mapping) => (
              <li key={mapping.source_member_id} className="flex items-center gap-3 py-2">
                <span className="flex-1 truncate">{mapping.source_display_name}</span>
                <span className={cn("num", toneClass(mapping.balance_base_cents))}>
                  {formatSigned(mapping.balance_base_cents, "USD")}
                </span>
                <span className="w-40 truncate text-right text-muted">
                  {mapping.target_display_name
                    ? `→ ${mapping.target_display_name} (${mapping.matched_by})`
                    : mapping.balance_base_cents === 0
                      ? "no balance to move"
                      : "no match — settle first"}
                </span>
              </li>
            ))}
          </ul>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={archive}
              onChange={(event) => setArchive(event.target.checked)}
              className="h-4 w-4 accent-[rgb(var(--accent))]"
            />
            Archive this group afterwards
          </label>
          <Button
            variant="primary"
            className="mt-3"
            disabled={!plan.ready || execute.isPending}
            onClick={() => execute.mutate()}
          >
            {plan.ready ? "Transfer balances" : "Someone still needs a match"}
          </Button>
        </>
      ) : null}
    </section>
  );
}

function Audit({ groupId }: { groupId: number }) {
  const entries = useQuery({
    queryKey: ["audit", groupId],
    queryFn: () => api.get<AuditEntry[]>(`/groups/${groupId}/audit?limit=100`),
  });

  if (entries.isPending) return <Spinner />;

  return (
    <ul className="divide-y divide-rule border-y border-rule text-sm">
      {(entries.data ?? []).map((entry) => (
        <li key={entry.id} className="py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span>
              <span className="font-medium">{entry.action}</span>{" "}
              <span className="text-muted">{entry.entity_type}</span>
              {entry.entity_id ? <span className="num text-muted"> #{entry.entity_id}</span> : null}
              {entry.origin !== "ui" ? (
                <span className="ml-2 rounded-full border border-rule px-1.5 text-2xs uppercase text-muted">
                  {entry.origin}
                </span>
              ) : null}
            </span>
            <span className="num text-xs text-muted">
              {new Date(entry.at).toLocaleString()}
            </span>
          </div>
          {entry.changed && Object.keys(entry.changed).length > 0 ? (
            <p className="mt-0.5 truncate text-xs text-muted">
              {Object.keys(entry.changed).slice(0, 6).join(", ")}
            </p>
          ) : null}
          {entry.note ? <p className="mt-0.5 text-xs text-muted">{entry.note}</p> : null}
        </li>
      ))}
      {(entries.data ?? []).length === 0 ? (
        <li className="py-3 text-muted">Nothing recorded yet.</li>
      ) : null}
    </ul>
  );
}
